import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { GenericService } from '../generic.service';
import { ChatRequest, ChatBotResponse } from '../../models/chat/chat-message.interface';
import { 
  ConversationResponse, 
  CreateConversationRequest, 
  ChatMessageResponse, 
  SendMessageRequest, 
  ChatUser,
  ChatMediaResponse
} from '../../models/chat/chat-api.models';
import { apiResponse } from '../../models/apiResponse';

@Injectable({
  providedIn: 'root'
})
export class ChatService {

  constructor(private genericService: GenericService) { }

  // 1. Creating & Fetching Conversations
  
  getConversations(skip: number = 0, take: number = 20): Observable<ConversationResponse[]> {
    return this.genericService.get<ConversationResponse[]>('chat/conversations', { params: { skip, take } });
  }

  createConversation(request: CreateConversationRequest): Observable<ConversationResponse> {
    return this.genericService.post<ConversationResponse>('chat/conversation', request);
  }

  // 2. Messages

  getMessages(conversationId: number, skip: number = 0, take: number = 50): Observable<ChatMessageResponse[]> {
    return this.genericService.get<ChatMessageResponse[]>(`chat/${conversationId}/messages`, { params: { skip, take } });
  }

  getPinnedMessages(conversationId: number): Observable<apiResponse<ChatMessageResponse[]>> {
    return this.genericService.get<apiResponse<ChatMessageResponse[]>>(`chat/${conversationId}/pinned`);
  }

  togglePin(request: { conversationId: number; messageId: number; isPinned: boolean }): Observable<apiResponse<ChatMessageResponse>> {
    return this.genericService.post<apiResponse<ChatMessageResponse>>('chat/pin', request);
  }

  searchMessages(conversationId: number, term: string): Observable<ChatMessageResponse[]> {
    return this.genericService.get<ChatMessageResponse[]>(`chat/${conversationId}/search`, { params: { term } });
  }

  getChatMedia(conversationId: number, type: 'Image' | 'File' | 'Link' | 'All' = 'All', skip: number = 0, take: number = 20): Observable<apiResponse<ChatMediaResponse[]>> {
    return this.genericService.get<apiResponse<ChatMediaResponse[]>>(`chat/${conversationId}/media`, { params: { type, skip, take } });
  }

  // 3. Sending Messages

  sendChatMessage(request: SendMessageRequest): Observable<ChatMessageResponse> {
    const formData = new FormData();
    formData.append('ConversationId', request.conversationId.toString());
    formData.append('Content', request.content || '');
    formData.append('MessageType', request.messageType.toString());
    
    if (request.attachmentUrl) {
        formData.append('AttachmentUrl', request.attachmentUrl);
    }
    if (request.metadata) {
        formData.append('Metadata', request.metadata);
    }
    if (request.file) {
        formData.append('File', request.file, request.file.name);
    }

    return this.genericService.postFormData<ChatMessageResponse>('chat/messages', formData);
  }

  // 4. Read Receipts

  markAsRead(conversationId: number): Observable<boolean> {
      return this.genericService.put<boolean>(`chat/conversations/${conversationId}/read`, {});
  }

  // Helper (Presumed needed for finding users to chat with)
  getChatUsers(searchText: string = '', pageNumber: number = 1, pageSize: number = 10): Observable<apiResponse<ChatUser[]>> {
    const params: any = { PageNumber: pageNumber, PageSize: pageSize };
    if (searchText) {
      params.SearchText = searchText;
    }
    return this.genericService.get<apiResponse<ChatUser[]>>('user/chat-users', { params });
  }
  sendMessage(chatRequest: ChatRequest): Observable<ChatBotResponse> {
    return this.genericService.post('chatbot', chatRequest);
  }
}
